import { useEffect, useState } from 'react';
import { gerarQrCodeDataUrl } from '../../utils/qrcode';
import { empresaAtiva } from '../../utils/empresa';

const LINK_PADRAO = typeof window !== 'undefined' ? window.location.origin : '';

interface CardMesaProps {
  numero: number;
  url: string;
}

function CardMesa({ numero, url }: CardMesaProps) {
  const [qrSrc, setQrSrc] = useState('');
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    let ativo = true;
    gerarQrCodeDataUrl(url).then((src) => {
      if (ativo) setQrSrc(src);
    });
    return () => {
      ativo = false;
    };
  }, [url]);

  const baixarPng = () => {
    if (!qrSrc) return;
    const link = document.createElement('a');
    link.download = `mesa-${numero}.png`;
    link.href = qrSrc;
    link.click();
  };

  const copiarLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      // fallback simples para navegadores sem clipboard API
      const campo = document.createElement('textarea');
      campo.value = url;
      document.body.appendChild(campo);
      campo.select();
      document.execCommand('copy');
      document.body.removeChild(campo);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    }
  };

  return (
    <div className="card flex flex-col items-center p-5 print:break-inside-avoid">
      <h3 className="text-lg font-semibold text-gray-100 mb-1">Mesa {numero}</h3>
      <p className="text-xs text-gray-400 mb-3">Escaneie para abrir a comanda</p>
      <div className="bg-white rounded-lg p-2 w-full max-w-[180px]">
        {qrSrc && (
          <img
            src={qrSrc}
            alt={`QR Code da Mesa ${numero}`}
            className="w-full h-auto block rounded"
          />
        )}
      </div>
      <div className="flex flex-wrap justify-center gap-2 mt-3 print:hidden">
        <button onClick={copiarLink} className="btn-secondary text-sm">
          {copiado ? '✓ Copiado!' : '📋 Copiar link'}
        </button>
        <button onClick={baixarPng} className="btn-secondary text-sm">
          Baixar PNG
        </button>
      </div>
    </div>
  );
}

export default function Tables() {
  const [quantidade, setQuantidade] = useState(6);
  const [linkBase, setLinkBase] = useState(LINK_PADRAO);
  const [linkValido, setLinkValido] = useState(true);

  const normalizarLink = (valor: string) => valor.replace(/\/+$/, '');

  const mudarQuantidade = (valor: string) => {
    const n = parseInt(valor, 10);
    if (Number.isInteger(n) && n >= 1 && n <= 30) {
      setQuantidade(n);
    }
  };

  const mudarLink = (valor: string) => {
    setLinkBase(valor);
    setLinkValido(valor.trim().length > 0);
  };

  const base = normalizarLink(linkBase.trim());
  // T120: o QR impresso TEM que identificar o restaurante — sem slug, o
  // pedido cairia no restaurante mais antigo da instalação.
  const slugAtivo = empresaAtiva()?.slug;
  const podeGerar = linkValido && !!slugAtivo;

  return (
    <div className="space-y-6">
      <div className="print:hidden">
        <h2 className="text-2xl font-bold text-gray-100 mb-2">🪑 Mesas e QR Codes</h2>
        <p className="text-gray-400 text-sm">
          Gere os QR Codes das mesas: o cliente escaneia e a comanda abre{' '}
          <strong className="text-gray-300">já com a mesa identificada</strong> —
          ele só digita o nome.
        </p>
        {/* T119: a mesa aqui é apenas o NÚMERO gravado no QR (não existe
            cadastro de mesas no sistema) — a tela não promete CRUD. */}
        <p className="text-xs text-gray-500 mt-1">
          A mesa é o número embutido no QR Code (não há cadastro de mesas): use o
          número que existe no seu salão. Se o salão tiver numeração irregular, gere
          os QR das mesas usadas e descarte os demais.
        </p>
      </div>

      <div className="card p-5 print:hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Quantidade de mesas
            </label>
            <input
              type="number"
              min={1}
              max={30}
              value={quantidade}
              onChange={(e) => mudarQuantidade(e.target.value)}
              className="input-field"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Link público do cardápio
            </label>
            <input
              type="text"
              value={linkBase}
              onChange={(e) => mudarLink(e.target.value)}
              className={`input-field ${linkValido ? '' : 'border-red-500'}`}
            />
            {!linkValido && (
              <p className="text-xs text-red-400 mt-1">
                Informe o link para gerar os QR Codes.
              </p>
            )}
          </div>
        </div>
        {!slugAtivo && (
          <p className="text-xs text-red-400 mt-2">
            Este QR precisa identificar o restaurante: entre com o restaurante
            selecionado (ou acesse por /restaurante/&lt;slug&gt;) antes de gerar e
            imprimir os QR Codes.
          </p>
        )}
        <p className="text-xs text-gray-500 mt-3">
          Dica: o link deve ser o endereço que o cliente acessa (ex:{' '}
          <span className="text-gray-400">{LINK_PADRAO}</span>). Se o endereço
          mudar algum dia, basta voltar aqui e regenerar — os QR Codes embutem
          o link atual.
        </p>
      </div>

      {podeGerar && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: quantidade }, (_, i) => i + 1).map((n) => (
              <CardMesa key={n} numero={n} url={`${base}?restaurante=${slugAtivo}&mesa=${n}`} />
            ))}
          </div>
          <div className="text-center print:hidden">
            <button
              onClick={() => window.print()}
              className="btn-primary"
            >
              Imprimir QR Codes
            </button>
          </div>
        </>
      )}
    </div>
  );
}